import { supabase } from '../lib/supabase';

/**
 * Shared CSV parser (same logic as bulkCsvCourseUploadService)
 */
function parseCSVLine(line) {
  const result = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line?.length; i++) {
    const char = line?.[i];
    if (char === '"') {
      if (inQuotes && line?.[i + 1] === '"') { current += '"'; i++; }
      else { inQuotes = !inQuotes; }
    } else if (char === ',' && !inQuotes) {
      result?.push(current?.trim());
      current = '';
    } else {
      current += char;
    }
  }
  result?.push(current?.trim());
  return result;
}

export function parseCsvFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e?.target?.result;
        const lines = text?.split(/\r?\n/)?.filter((l) => l?.trim());
        if (lines?.length < 2) {
          reject(new Error('CSV must have a header row and at least one data row'));
          return;
        }
        const headers = parseCSVLine(lines[0])?.map((h) =>
          h?.trim()?.toLowerCase()?.replace(/\s+/g, '_')
        );
        let rows = [];
        for (let i = 1; i < lines?.length; i++) {
          const values = parseCSVLine(lines[i]);
          if (values?.every((v) => !v?.trim())) continue;
          const row = {};
          headers?.forEach((h, idx) => { row[h] = values?.[idx] ?? ''; });
          row._rowNumber = i + 1;
          rows?.push(row);
        }
        resolve(rows);
      } catch (err) {
        reject(new Error(`Failed to parse CSV: ${err?.message}`));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file, 'UTF-8');
  });
}

/**
 * Two-step institute lookup: name on institute_profiles, city on users
 */
async function findInstituteByNameAndCity(name, city) {
  const { data: institutes, error: instError } = await supabase
    ?.from('institute_profiles')
    ?.select('id, institute_name, user_id')
    ?.ilike('institute_name', name?.trim())
    ?.limit(50);
  if (instError) throw instError;
  if (!institutes || institutes?.length === 0) return null;

  const userIds = institutes?.map((i) => i?.user_id)?.filter(Boolean);
  if (userIds?.length === 0) return null;

  const { data: users, error: userError } = await supabase
    ?.from('users')
    ?.select('id, city')
    ?.in('id', userIds);
  if (userError) throw userError;

  const cityLower = city?.trim()?.toLowerCase();
  const userCityMap = {};
  (users || [])?.forEach((u) => {
    userCityMap[u?.id] = u?.city?.toLowerCase()?.trim() ?? '';
  });

  return institutes?.find((inst) => userCityMap?.[inst?.user_id] === cityLower) || null;
}

// ─────────────────────────────────────────────────────────────
// BULK FAQ UPLOAD
// ─────────────────────────────────────────────────────────────

const FAQ_REQUIRED = ['institute_name', 'city', 'question', 'category', 'is_active'];
const FAQ_VALID_CATEGORIES = ['Admissions', 'Courses', 'Fees', 'Campus Life', 'Placements', 'Scholarships', 'Facilities', 'General'];

export function downloadSampleFaqCsv() {
  // Vertical format: question row followed by answer row below it
  // Columns: institute_name, city, question, category, is_active
  // Answer row: (empty), (empty), answer_text, (empty), (empty)
  let rows = [
    ['institute_name', 'city', 'question', 'category', 'is_active'],
    ['Amrita School of Engineering', 'Coimbatore', 'What is the admission process?', 'Admissions', 'true'],
    ['', '', 'Apply online via the official portal with your entrance exam scores.', '', ''],
    ['Amrita School of Engineering', 'Coimbatore', 'What are the hostel facilities?', 'Campus Life', 'true'],
    ['', '', 'Separate hostels for boys and girls with all amenities.', '', ''],
    ['Anna University', 'Chennai', 'What scholarships are available?', 'Scholarships', 'true'],
    ['', '', 'Merit-based and need-based scholarships are available for eligible students.', '', ''],
  ];
  const csv = rows?.map((r) => r?.map((c) => `"${String(c)?.replace(/"/g, '""')}"`)?.join(','))?.join('\r\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = 'bulk_faq_template.csv';
  document.body?.appendChild(a); a?.click();
  document.body?.removeChild(a); URL.revokeObjectURL(url);
}

export async function bulkFaqUpload(file, { onProgress } = {}) {
  let rows;
  try {
    onProgress?.({ phase: 'parsing' });
    rows = await parseCsvFile(file);
  } catch (err) {
    return { fatalError: err?.message };
  }

  // Validate headers — answer column is no longer required in header
  const headers = Object.keys(rows?.[0] || {})?.filter((h) => h !== '_rowNumber');
  const missing = FAQ_REQUIRED?.filter((f) => !headers?.includes(f));
  if (missing?.length > 0) {
    return { fatalError: `Missing required columns: ${missing?.join(', ')}. Download the sample template.` };
  }

  // Parse vertical format: pair each question row with the answer row below it
  // A question row has institute_name + city filled; an answer row has them empty
  // The answer text sits in the "question" column of the answer row
  const faqPairs = [];
  let i = 0;
  while (i < rows?.length) {
    const row = rows?.[i];
    const instituteName = String(row?.institute_name ?? '')?.trim();
    const city = String(row?.city ?? '')?.trim();
    const questionText = String(row?.question ?? '')?.trim();

    if (instituteName && city && questionText) {
      // This is a question row — look ahead for the answer row
      const nextRow = rows?.[i + 1];
      const nextInstitute = String(nextRow?.institute_name ?? '')?.trim();
      const nextCity = String(nextRow?.city ?? '')?.trim();
      const answerText = String(nextRow?.question ?? '')?.trim(); // answer is in the question column of next row

      if (nextRow && !nextInstitute && !nextCity && answerText) {
        // Valid pair: question row + answer row below
        faqPairs?.push({
          _rowNumber: row?._rowNumber,
          institute_name: instituteName,
          city,
          question: questionText,
          answer: answerText,
          category: String(row?.category ?? '')?.trim(),
          is_active: String(row?.is_active ?? 'true')?.trim(),
        });
        i += 2; // skip both rows
      } else {
        // Question row without a valid answer row below — treat as error
        faqPairs?.push({
          _rowNumber: row?._rowNumber,
          institute_name: instituteName,
          city,
          question: questionText,
          answer: '',
          category: String(row?.category ?? '')?.trim(),
          is_active: String(row?.is_active ?? 'true')?.trim(),
          _missingAnswer: true,
        });
        i += 1;
      }
    } else {
      // Orphan answer row (no preceding question) — skip
      i += 1;
    }
  }

  if (faqPairs?.length === 0) {
    return { fatalError: 'No valid FAQ pairs found. Make sure each question row is immediately followed by its answer row.' };
  }

  onProgress?.({ phase: 'uploading', rowIndex: 0, totalRows: faqPairs?.length });

  const results = [];
  let uploaded = 0, failed = 0, skipped = 0;

  for (let j = 0; j < faqPairs?.length; j++) {
    const pair = faqPairs?.[j];
    const { institute_name: instituteName, city, question, answer, category, is_active, _rowNumber, _missingAnswer } = pair;

    onProgress?.({ phase: 'uploading', rowIndex: j + 1, totalRows: faqPairs?.length, instituteName, city });

    if (_missingAnswer || !answer) {
      results?.push({ row: _rowNumber, instituteName, city, status: 'failed', message: 'Answer row missing below this question' });
      failed++;
      continue;
    }

    if (!instituteName || !city || !question || !category) {
      results?.push({ row: _rowNumber, instituteName, city, status: 'failed', message: 'Missing required fields' });
      failed++;
      continue;
    }

    if (!FAQ_VALID_CATEGORIES?.includes(category)) {
      results?.push({ row: _rowNumber, instituteName, city, status: 'failed', message: `Invalid category "${category}". Valid: ${FAQ_VALID_CATEGORIES?.join(', ')}` });
      failed++;
      continue;
    }

    let institute;
    try {
      institute = await findInstituteByNameAndCity(instituteName, city);
    } catch (err) {
      results?.push({ row: _rowNumber, instituteName, city, status: 'failed', message: `Database error: ${err?.message}` });
      failed++;
      continue;
    }

    if (!institute) {
      results?.push({ row: _rowNumber, instituteName, city, status: 'not_found', message: `Institute "${instituteName}, ${city}" not found` });
      skipped++;
      continue;
    }

    try {
      const { error } = await supabase?.from('faqs')?.insert({
        institute_id: institute?.user_id,
        question,
        answer,
        category,
        display_order: 0,
        is_active: String(is_active)?.toLowerCase() !== 'false',
      });
      if (error) throw error;
      results?.push({ row: _rowNumber, instituteName, city, status: 'success', message: 'FAQ added' });
      uploaded++;
    } catch (err) {
      results?.push({ row: _rowNumber, instituteName, city, status: 'failed', message: err?.message });
      failed++;
    }
  }

  return {
    results,
    summary: { totalRows: faqPairs?.length, totalRowsUploaded: uploaded, totalRowsFailed: failed, totalRowsSkipped: skipped },
  };
}

// ─────────────────────────────────────────────────────────────
// BULK DESCRIPTION / ABOUT US UPLOAD
// ─────────────────────────────────────────────────────────────

const DESC_REQUIRED = ['institute_name', 'city', 'description'];

export function downloadSampleDescriptionCsv() {
  let rows = [
    ['institute_name', 'city', 'description'],
    ['Amrita School of Engineering', 'Coimbatore', 'Amrita School of Engineering, Coimbatore is a premier engineering institution known for its research excellence and industry-oriented curriculum. Established in 1994, it offers undergraduate, postgraduate, and doctoral programs across various engineering disciplines.'],
    ['Anna University', 'Chennai', 'Anna University is a technical university in Tamil Nadu, India. It offers higher education in Engineering, Technology, and allied Sciences relevant to the current and projected needs of society.'],
  ];
  const csv = rows?.map((r) => r?.map((c) => `"${String(c)?.replace(/"/g, '""')}"`)?.join(','))?.join('\r\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = 'bulk_description_template.csv';
  document.body?.appendChild(a); a?.click();
  document.body?.removeChild(a); URL.revokeObjectURL(url);
}

export async function bulkDescriptionUpload(file, { onProgress } = {}) {
  let rows;
  try {
    onProgress?.({ phase: 'parsing' });
    rows = await parseCsvFile(file);
  } catch (err) {
    return { fatalError: err?.message };
  }

  const headers = Object.keys(rows?.[0] || {})?.filter((h) => h !== '_rowNumber');
  const missing = DESC_REQUIRED?.filter((f) => !headers?.includes(f));
  if (missing?.length > 0) {
    return { fatalError: `Missing required columns: ${missing?.join(', ')}. Download the sample template.` };
  }

  onProgress?.({ phase: 'uploading', rowIndex: 0, totalRows: rows?.length });

  const results = [];
  let uploaded = 0, failed = 0, skipped = 0;

  for (let i = 0; i < rows?.length; i++) {
    const row = rows?.[i];
    const instituteName = String(row?.institute_name ?? '')?.trim();
    const city = String(row?.city ?? '')?.trim();
    const description = String(row?.description ?? '')?.trim();

    onProgress?.({ phase: 'uploading', rowIndex: i + 1, totalRows: rows?.length, instituteName, city });

    if (!instituteName || !city || !description) {
      results?.push({ row: row?._rowNumber, instituteName, city, status: 'failed', message: 'Missing required fields' });
      failed++;
      continue;
    }

    let institute;
    try {
      institute = await findInstituteByNameAndCity(instituteName, city);
    } catch (err) {
      results?.push({ row: row?._rowNumber, instituteName, city, status: 'failed', message: `Database error: ${err?.message}` });
      failed++;
      continue;
    }

    if (!institute) {
      results?.push({ row: row?._rowNumber, instituteName, city, status: 'not_found', message: `Institute "${instituteName}, ${city}" not found` });
      skipped++;
      continue;
    }

    try {
      const { error } = await supabase
        ?.from('institute_profiles')
        ?.update({ description })
        ?.eq('id', institute?.id);
      if (error) throw error;
      results?.push({ row: row?._rowNumber, instituteName, city, status: 'success', message: 'Description updated' });
      uploaded++;
    } catch (err) {
      results?.push({ row: row?._rowNumber, instituteName, city, status: 'failed', message: err?.message });
      failed++;
    }
  }

  return {
    results,
    summary: { totalRows: rows?.length, totalRowsUploaded: uploaded, totalRowsFailed: failed, totalRowsSkipped: skipped },
  };
}

// ─────────────────────────────────────────────────────────────
// BULK TESTIMONIAL UPLOAD
// ─────────────────────────────────────────────────────────────

const TESTIMONIAL_REQUIRED = ['institute_name', 'city', 'student_name', 'student_program', 'rating', 'content'];

export function downloadSampleTestimonialCsv() {
  let rows = [
    ['institute_name', 'city', 'student_name', 'student_program', 'student_year', 'rating', 'content', 'is_featured', 'is_approved'],
    ['Amrita School of Engineering', 'Coimbatore', 'Rahul Sharma', 'B.Tech Computer Science', '2023', '5', 'Excellent faculty and great placement support. Highly recommend!', 'true', 'true'],
    ['Amrita School of Engineering', 'Coimbatore', 'Priya Nair', 'M.Tech VLSI Design', '2022', '4', 'World-class research facilities and supportive professors.', 'false', 'true'],
    ['Anna University', 'Chennai', 'Karthik Rajan', 'B.E. Civil Engineering', '2023', '5', 'Great infrastructure and industry exposure through internships.', 'true', 'true'],
  ];
  const csv = rows?.map((r) => r?.map((c) => `"${String(c)?.replace(/"/g, '""')}"`)?.join(','))?.join('\r\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = 'bulk_testimonials_template.csv';
  document.body?.appendChild(a); a?.click();
  document.body?.removeChild(a); URL.revokeObjectURL(url);
}

export async function bulkTestimonialUpload(file, { onProgress } = {}) {
  let rows;
  try {
    onProgress?.({ phase: 'parsing' });
    rows = await parseCsvFile(file);
  } catch (err) {
    return { fatalError: err?.message };
  }

  const headers = Object.keys(rows?.[0] || {})?.filter((h) => h !== '_rowNumber');
  const missing = TESTIMONIAL_REQUIRED?.filter((f) => !headers?.includes(f));
  if (missing?.length > 0) {
    return { fatalError: `Missing required columns: ${missing?.join(', ')}. Download the sample template.` };
  }

  onProgress?.({ phase: 'uploading', rowIndex: 0, totalRows: rows?.length });

  const results = [];
  let uploaded = 0, failed = 0, skipped = 0;

  for (let i = 0; i < rows?.length; i++) {
    const row = rows?.[i];
    const instituteName = String(row?.institute_name ?? '')?.trim();
    const city = String(row?.city ?? '')?.trim();
    const studentName = String(row?.student_name ?? '')?.trim();
    const studentProgram = String(row?.student_program ?? '')?.trim();
    const studentYear = String(row?.student_year ?? '')?.trim();
    const ratingRaw = String(row?.rating ?? '')?.trim();
    const content = String(row?.content ?? '')?.trim();

    onProgress?.({ phase: 'uploading', rowIndex: i + 1, totalRows: rows?.length, instituteName, city });

    if (!instituteName || !city || !studentName || !studentProgram || !ratingRaw || !content) {
      results?.push({ row: row?._rowNumber, instituteName, city, status: 'failed', message: 'Missing required fields' });
      failed++;
      continue;
    }

    const rating = parseInt(ratingRaw);
    if (isNaN(rating) || rating < 1 || rating > 5) {
      results?.push({ row: row?._rowNumber, instituteName, city, status: 'failed', message: `Invalid rating "${ratingRaw}" — must be 1–5` });
      failed++;
      continue;
    }

    let institute;
    try {
      institute = await findInstituteByNameAndCity(instituteName, city);
    } catch (err) {
      results?.push({ row: row?._rowNumber, instituteName, city, status: 'failed', message: `Database error: ${err?.message}` });
      failed++;
      continue;
    }

    if (!institute) {
      results?.push({ row: row?._rowNumber, instituteName, city, status: 'not_found', message: `Institute "${instituteName}, ${city}" not found` });
      skipped++;
      continue;
    }

    try {
      const { error } = await supabase?.from('testimonials')?.insert({
        institute_id: institute?.user_id,
        student_name: studentName,
        student_program: studentProgram,
        student_year: studentYear || null,
        rating,
        content,
        is_featured: String(row?.is_featured)?.toLowerCase() === 'true',
        is_approved: String(row?.is_approved)?.toLowerCase() !== 'false',
        display_order: parseInt(row?.display_order) || 0,
      });
      if (error) throw error;
      results?.push({ row: row?._rowNumber, instituteName, city, status: 'success', message: 'Testimonial added' });
      uploaded++;
    } catch (err) {
      results?.push({ row: row?._rowNumber, instituteName, city, status: 'failed', message: err?.message });
      failed++;
    }
  }

  return {
    results,
    summary: { totalRows: rows?.length, totalRowsUploaded: uploaded, totalRowsFailed: failed, totalRowsSkipped: skipped },
  };
}
